import React from 'react';
import {
  Dashboard as DashboardIcon,
  FolderSpecial as FolderSpecialIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Flag as FlagIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { ManualSection, AppManualContent } from '../../Commun/components/UserManual';

// ─────────────────────────────────────────────────────────────────────────────
// ProjectManagement — Section definitions
// ─────────────────────────────────────────────────────────────────────────────

export const PROJECT_SECTIONS: ManualSection[] = [
  { id: 'dashboard',  icon: <DashboardIcon />,           color: '#4f46e5' },
  { id: 'projects',   icon: <FolderSpecialIcon />,        color: '#0d9488' },
  { id: 'tasks',      icon: <AssignmentTurnedInIcon />,   color: '#7c3aed' },
  { id: 'milestones', icon: <FlagIcon />,                 color: '#d97706' },
  { id: 'team',       icon: <GroupsIcon />,               color: '#ea580c' },
];

// ─────────────────────────────────────────────────────────────────────────────
// ProjectManagement — Multilingual content (FR / EN / AR)
// ─────────────────────────────────────────────────────────────────────────────

export const PROJECT_CONTENT: AppManualContent = {
  fr: {
    dashboard: {
      title: 'Tableau de Bord du Portefeuille de Projets',
      description: 'Centre de pilotage stratégique et visuel pour le suivi global de tous les projets de l\'entreprise. Affiche en direct les indicateurs clés de performance (KPI), l\'état de consommation des budgets, l\'avancement global des chantiers et les alertes de retard.',
      features: [
        'KPI paramétrables en direct : Projets totaux, Projets en cours, Tâches terminées, Tâches en retard',
        'Suivi financier consolidé : Budget total alloué vs. Dépenses réelles engagées',
        'Barres de progression visuelles avec calcul du taux d\'avancement moyen (%)',
        'Détection automatique des dépassements budgétaires avec badge d\'alerte prioritaire',
        'Widget des tâches urgentes nécessitant une intervention immédiate',
        'Personnalisation des indicateurs selon le profil utilisateur via le gestionnaire de KPI'
      ],
      tips: [
        'Un badge rouge "Dépassement" signale que les dépenses réelles excèdent le budget alloué — analysez les postes de coûts associés.',
        'Utilisez le bouton "Voir tout" pour naviguer instantanément vers le portefeuille de projets ou la liste des tâches.',
        'Les tâches en retard apparaissent en surbrillance pour faciliter la priorisation de vos réunions de coordination.'
      ],
      steps: [
        'Consultez les 8 cartes KPI supérieures pour apprécier la santé globale de votre portefeuille.',
        'Vérifiez le widget des projets en cours et repérez d\'éventuels dépassements de budget.',
        'Examinez la liste des tâches prioritaires pour planifier les actions urgentes de la journée.',
        'Cliquez sur "Voir tout" pour basculer vers la gestion complète des projets ou des tâches.'
      ]
    },
    projects: {
      title: 'Portefeuille de Projets',
      description: 'Gestion exhaustive du cycle de vie des projets de l\'entreprise : cadrage initial, planification budgétaire, affectation des chefs de projet, suivi de la rentabilité et clôture formelle.',
      features: [
        'Double mode d\'affichage : Vue Grille avec cartes dynamiques ou Vue Tableau tabulaire dense',
        'Cycle de vie normalisé : Planification, En cours, En pause, Terminé, Annulé',
        'Niveaux de priorité gradués : Basse, Moyenne, Haute, Urgente',
        'Gestion financière intégrée : Budget alloué, Dépenses réelles et calcul du solde restant',
        'Planification calendaire : Date de début, date de livraison prévue et suivi des délais',
        'Rattachement du chef de projet responsable et identification du client / bénéficiaire'
      ],
      tips: [
        'Un projet bien cadré avec un budget précis permet de prévenir 80% des dérives financières.',
        'Passez un projet en statut "En pause" plutôt que de le supprimer pour conserver tout l\'historique d\'exécution.',
        'Utilisez la recherche et les filtres de statut pour isoler rapidement les projets actifs d\'un client.'
      ],
      steps: [
        'Cliquez sur "Nouveau Projet" pour ouvrir le formulaire de création.',
        'Définissez le titre, le client, le chef de projet responsable et la priorité.',
        'Renseignez les dates de début et de livraison prévue ainsi que le budget total alloué.',
        'Validez pour enregistrer la fiche projet et initialiser son espace de travail.',
        'Actualisez régulièrement le pourcentage d\'avancement et les dépenses réelles au fil de l\'exécution.'
      ]
    },
    tasks: {
      title: 'Gestion des Tâches & Activités',
      description: 'Planification opérationnelle et répartition des charges de travail de l\'équipe. Permet de découper les projets en tâches élémentaires, d\'attribuer les responsabilités et de contrôler les temps passés.',
      features: [
        'Cycle de vie des tâches en 5 états : À faire, En cours, En revue, Terminée, Bloquée',
        'Niveaux de priorité : Basse, Moyenne, Haute, Critique',
        'Estimation et suivi des temps : Heures prévues vs. Heures réelles passées avec calcul d\'écart',
        'Affectation directe et nominative à un collaborateur ou membre d\'équipe',
        'Date d\'échéance avec détection automatique des retards',
        'Rattachement obligatoire au projet parent avec répercussion sur l\'avancement global'
      ],
      tips: [
        'Si une tâche dépend d\'un livrable externe en attente, passez-la immédiatement au statut "Bloquée" pour alerter le chef de projet.',
        'La comparaison entre heures estimées et heures réelles affine la précision de vos devis futurs.',
        'Filtrez par statut "À faire" et par responsable pour organiser votre plan de charge journalier.'
      ],
      steps: [
        'Cliquez sur "Nouvelle Tâche" et sélectionnez le projet concerné.',
        'Donnez un intitulé précis, sélectionnez le collaborateur assigné et fixez la date limite.',
        'Estimez le volume horaire requis (ex: 8 heures).',
        'Au fur et à mesure du travail, mettez à jour le pourcentage d\'avancement et les heures réelles passées.',
        'Passez le statut à "Terminée" dès la validation des livrables.'
      ]
    },
    milestones: {
      title: 'Jalons & Objectifs Clés',
      description: 'Suivi des étapes charnières et points de contrôle majeurs de chaque projet. Assure le respect des engagements contractuels et valide les passages d\'une phase à la suivante.',
      features: [
        'Statuts de jalon normalisés : En attente, Atteint, En retard',
        'Échéance cible avec calcul des jours restants avant le jalon',
        'Rattachement direct au projet et identification des livrables associés',
        'Visualisation chronologique des échéances majeures du portefeuille',
        'Fiche descriptive des critères d\'acceptation requis pour valider le jalon'
      ],
      tips: [
        'Un projet type ne devrait pas comporter plus de 4 à 8 jalons majeurs pour rester lisible et stratégique.',
        'Tout jalon non atteint à sa date d\'échéance bascule automatiquement en "En retard" et alerte la direction.',
        'Associez la validation d\'un jalon à l\'émission d\'une tranche de facturation pour sécuriser la trésorerie.'
      ],
      steps: [
        'Créez un jalon en lui attribuant un titre représentatif (ex: "Validation CCTP", "Livraison Lot 1", "Réception Provisoire").',
        'Liez le jalon à son projet et fixez la date limite contractuelle.',
        'Détaillez les conditions d\'acceptation dans la description.',
        'Une fois les validations formelles obtenues, passez le statut à "Atteint".'
      ]
    },
    team: {
      title: 'Équipe & Ressources Projet',
      description: 'Organisation du capital humain et affectation des compétences aux différents projets. Permet d\'encadrer les rôles, de mesurer les taux d\'allocation et de prévenir la surcharge des collaborateurs.',
      features: [
        'Répertoire des membres affectés par projet avec rôle explicite (Chef de projet, Ingénieur, Technicien)',
        'Taux d\'allocation prévisionnel en pourcentage (% de temps dédié au projet)',
        'Coordonnées de contact professionnelles (Email, Téléphone)',
        'Vue consolidée de la disponibilité des ressources de l\'organisation',
        'Rattachement multi-projets avec suivi de la charge globale de travail'
      ],
      tips: [
        'Veillez à ce que la somme des allocations d\'un collaborateur à travers tous ses projets ne dépasse pas 100%.',
        'Identifiez clairement le chef de projet référent pour chaque équipe afin de fluidifier la prise de décision.',
        'Mettez à jour le taux d\'allocation lors des phases de pic de charge pour équilibrer les efforts.'
      ],
      steps: [
        'Cliquez sur "Nouveau Membre" pour affecter une ressource à un projet.',
        'Sélectionnez le projet d\'affectation et saisissez le nom complet et l\'email du collaborateur.',
        'Spécifiez son rôle exact sur le projet.',
        'Définissez son taux d\'allocation (ex: 50% pour un mi-temps, 100% pour une mobilisation exclusive).',
        'Enregistrez pour valider l\'affectation de la ressource.'
      ]
    }
  },
  en: {
    dashboard: {
      title: 'Project Portfolio Dashboard',
      description: 'Strategic and visual command center for comprehensive tracking of all corporate projects. Displays live key performance indicators (KPIs), budget consumption status, overall project progress, and critical delay warnings.',
      features: [
        'Configurable live KPIs: Total Projects, Active Projects, Done Tasks, Overdue Tasks',
        'Consolidated financial tracking: Total Allocated Budget vs. Actual Incurred Expenses',
        'Visual progress bars calculating average project completion rate (%)',
        'Automated budget overrun detection with high-visibility alert badges',
        'Urgent tasks widget spotlighting assignments requiring immediate action',
        'Customizable metrics per user profile through the centralized KPI manager'
      ],
      tips: [
        'A red "Over Budget" badge signals that actual spent exceeds allocated funding — investigate itemized project costs.',
        'Use the "View All" button to jump directly to the complete project portfolio or task tracker.',
        'Overdue tasks appear highlighted to streamline agenda planning during weekly coordination meetings.'
      ],
      steps: [
        'Review the 8 top KPI cards to evaluate the overall health of your project portfolio.',
        'Inspect the active projects widget to spot potential budget overruns early.',
        'Review the priority task backlog to schedule critical daily assignments.',
        'Click "View All" to transition to detailed project or task management pages.'
      ]
    },
    projects: {
      title: 'Project Portfolio Management',
      description: 'Comprehensive lifecycle management for enterprise projects: initial scoping, budget allocation, project manager assignment, profitability monitoring, and formal closure.',
      features: [
        'Dual view modes: Responsive Grid with dynamic cards or dense tabular data view',
        'Standardized lifecycle phases: Planning, In Progress, On Hold, Completed, Cancelled',
        'Graduated priority levels: Low, Medium, High, Urgent',
        'Integrated financial management: Allocated budget, Actual spent, and remaining variance',
        'Timeline scheduling: Start date, target completion date, and milestone countdowns',
        'Assignment of responsible Project Manager and client/beneficiary designation'
      ],
      tips: [
        'A well-scoped project charter with realistic budgeting prevents 80% of cost overruns.',
        'Place projects "On Hold" instead of deleting them to preserve execution records and audits.',
        'Use search and status filters to quickly isolate all active initiatives for a specific client.'
      ],
      steps: [
        'Click "New Project" to open the creation form.',
        'Define project title, client, responsible manager, and priority tier.',
        'Enter kickoff and target delivery dates along with the total financial budget.',
        'Save to register the project and initialize its execution workspace.',
        'Update progress percentage and actual expenses regularly as work advances.'
      ]
    },
    tasks: {
      title: 'Tasks & Activities Management',
      description: 'Operational planning and team workload distribution. Break down projects into actionable tasks, assign individual responsibilities, and monitor actual vs. estimated hours.',
      features: [
        '5-stage task lifecycle: To Do, In Progress, Review, Done, Blocked',
        'Priority scoring: Low, Medium, High, Critical',
        'Time tracking: Estimated hours vs. Actual hours logged with variance analytics',
        'Direct assignment to designated team members and specialists',
        'Due dates with automated deadline slip detection and visual warnings',
        'Mandatory link to parent project with automatic roll-up to overall project progress'
      ],
      tips: [
        'If a task depends on pending external deliverables, set its status to "Blocked" to alert leadership.',
        'Tracking estimated vs. actual logged hours sharpens estimation accuracy for future contracts.',
        'Filter by "To Do" status and assignee to plan daily team standups effectively.'
      ],
      steps: [
        'Click "New Task" and select the associated project.',
        'Provide a clear action title, assign an owner, and specify the deadline.',
        'Estimate required effort hours (e.g., 8 hours).',
        'Update progress percentage and record actual hours as execution proceeds.',
        'Advance status to "Done" once deliverable acceptance criteria are satisfied.'
      ]
    },
    milestones: {
      title: 'Milestones & Key Deliverables',
      description: 'Tracking of pivotal project stages and major contractual checkpoints. Ensures adherence to delivery commitments and authorizes phase transitions.',
      features: [
        'Standardized milestone statuses: Pending, Reached, Delayed',
        'Target deadline with automated countdown calculation of days remaining',
        'Direct linkage to parent project and associated contractual deliverables',
        'Chronological roadmap view of all major organizational checkpoints',
        'Documentation of formal acceptance criteria required for milestone sign-off'
      ],
      tips: [
        'A standard project should define between 4 and 8 major milestones to stay clear and strategic.',
        'Any milestone unreached by its target date automatically shifts to "Delayed" and alerts management.',
        'Tie milestone sign-offs to contractual billing phases to protect project cash flow.'
      ],
      steps: [
        'Create a milestone with a representative title (e.g., "Design Freeze", "Phase 1 Handover", "Final Acceptance").',
        'Link the milestone to its project and set the contractual due date.',
        'Detail acceptance prerequisites in the description field.',
        'Once formal sign-off is secured, advance status to "Reached".'
      ]
    },
    team: {
      title: 'Team & Project Resources',
      description: 'Human capital management and skills assignment across enterprise projects. Define roles, measure allocation rates, and prevent team member overallocation.',
      features: [
        'Directory of team members assigned per project with clear roles (Project Manager, Engineer, Specialist)',
        'Planned resource allocation rate expressed as a percentage (% time dedicated to project)',
        'Professional contact coordinates (Direct email, Phone extension)',
        'Consolidated organizational view of staff availability and assignments',
        'Multi-project assignment tracking to identify and mitigate team burnout'
      ],
      tips: [
        'Ensure that a team member\'s cumulative allocation across all concurrent projects does not exceed 100%.',
        'Clearly identify the lead Project Manager for each team to streamline escalation pathways.',
        'Adjust allocation percentages during intense delivery phases to balance operational workload.'
      ],
      steps: [
        'Click "New Member" to allocate a professional resource to a project.',
        'Select the target project and provide the member\'s full name and email.',
        'Specify their exact role and scope of responsibility on the project.',
        'Define their allocation percentage (e.g., 50% for half-time, 100% for full dedicated focus).',
        'Save to register the staffing allocation.'
      ]
    }
  },
  ar: {
    dashboard: {
      title: 'لوحة قيادة محفظة المشاريع',
      description: 'مركز قيادة استراتيجي ومرئي للمتابعة الشاملة لجميع مشاريع المؤسسة. يعرض مؤشرات الأداء الرئيسية اللحظية (KPIs)، حالة استهلاك الميزانيات، نسبة التقدم العام، وتنبيهات التأخير الحرجة.',
      features: [
        'مؤشرات أداء لحظية قابلة للتهيئة: إجمالي المشاريع، المشاريع الجارية، المهام المنجزة، والمهام المتأخرة',
        'متابعة مالية مجمعة: الميزانية الإجمالية المخصصة مقابل المصروفات الفعلية الملتزم بها',
        'أشرطة تقدم مرئية تحسب متوسط معدل إنجاز المشاريع المئوي (%)',
        'كشف تلقائي عن تجاوزات الميزانية مع شارة تنبيهية ذات أولوية',
        'قسم خاص بالمهام العاجلة التي تتطلب تدخلاً فورياً',
        'إمكانية تخصيص المؤشرات حسب ملف المستخدم عبر أداة إدارة المؤشرات'
      ],
      tips: [
        'تشير شارة "تجاوز" الحمراء إلى أن النفقات الفعلية فاقت الميزانية المعتمدة — راجع بنود التكاليف فوراً.',
        'استخدم زر "عرض الكل" للانتقال السريع إلى محفظة المشاريع الكاملة أو سجل المهام.',
        'تبرز المهام المتأخرة بلون تحذيري لتسهيل ترتيب الأولويات أثناء اجتماعات التنسيق الدورية.'
      ],
      steps: [
        'راجع بطاقات مؤشرات الأداء الـ 8 في الأعلى لتقييم الوضع العام لمحفظة مشاريعك.',
        'تفقد قسم المشاريع الجارية واكتشف أي تجاوزات مالية محتملة مبكراً.',
        'اطلع على قائمة المهام ذات الأولوية لتخطيط الإجراءات العاجلة خلال اليوم.',
        'انقر على "عرض الكل" للانتقال إلى الإدارة الشاملة للمشاريع أو المهام.'
      ]
    },
    projects: {
      title: 'إدارة محفظة المشاريع',
      description: 'إدارة شاملة لدورة حياة مشاريع المؤسسة: التأطير الأولي، التخطيط المالي للميزانية، تعيين مديري المشاريع، متابعة الربحية، والإغلاق الرسمي للمشروع.',
      features: [
        'عرض مزدوج ومرن: عرض شبكي بالبطاقات التفاعلية أو عرض جدولي مفصل',
        'مراحل معيارية لدورة حياة المشروع: التخطيط، قيد الإنجاز، متوقف مؤقتاً، مكتمل، ملغى',
        'مستويات أولوية متدرجة: منخفضة، متوسطة، عالية، عاجلة',
        'إدارة مالية متكاملة: الميزانية المخصصة، المصروفات الفعلية وحساب الرصيد المتبقي',
        'جدولة زمنية دقيقة: تاريخ الانطلاق، تاريخ التسليم التعاقدي ومتابعة المهل الزمنية',
        'تعيين مدير المشروع المسؤول وتحديد العميل أو الجهة المستفيدة'
      ],
      tips: [
        'المشروع ذو النطاق المحدد والميزانية الدقيقة يتفادى 80% من الانحرافات المالية والزمنية.',
        'حول حالة المشروع إلى "متوقف مؤقتاً" بدلاً من حذفه للاحتفاظ بكامل سجل التنفيذ والتدقيق.',
        'استخدم أدوات البحث والفرز لعزل كافة المشاريع النشطة لعميل معين بسرعة.'
      ],
      steps: [
        'انقر على "مشروع جديد" لفتح استمارة إنشاء المشروع.',
        'حدد عنوان المشروع، العميل، مدير المشروع المسؤول ومستوى الأولوية.',
        'أدخل تاريخ البداية وتاريخ التسليم المستهدف بالإضافة إلى الميزانية المالية الإجمالية.',
        'احفظ لتسجيل بطاقة المشروع وتهيئة مساحة العمل الخاصة به.',
        'قم بتحديث نسبة التقدم والمصروفات الفعلية بانتظام مع تقدم سير الأعمال.'
      ]
    },
    tasks: {
      title: 'إدارة المهام والأنشطة',
      description: 'التخطيط التشغيلي وتوزيع أعباء العمل على فريق المشروع. تقسيم المشاريع إلى مهام تنفيذية محددة، تعيين المسؤوليات، ومراقبة الساعات الفعلية مقابل التقديرية.',
      features: [
        'دورة حياة المهام في 5 مراحل: قيد الانتظار، قيد التنفيذ، قيد المراجعة، منجزة، معطلة',
        'مستويات الأولوية: منخفضة، متوسطة، عالية، حرجة',
        'متابعة زمنية دقيقة: الساعات المقدرة مقابل الساعات الفعلية مع حساب الفروقات',
        'إسناد مباشر واسمي لعضو محدد في فريق العمل',
        'تاريخ استحقاق مع كشف تلقائي عن التأخيرات',
        'ربط إلزامي بالمشروع الأصلي مع تأثير تلقائي على نسبة الإنجاز العامة'
      ],
      tips: [
        'إذا كانت المهمة معلقة على مخرجات خارجية لم تكتمل بعد، حول حالتها فوراً إلى "معطلة" لتنبيه مدير المشروع.',
        'مقارنة الساعات المقدرة بالساعات الفعلية تزيد من دقة العروض المالية والتقديرات المستقبلية.',
        'قم بتصفية المهام حسب حالة "قيد الانتظار" والمسؤول لتنظيم خطة العمل اليومية بفاعلية.'
      ],
      steps: [
        'انقر على "مهمة جديدة" واختر المشروع المعني.',
        'اكتب عنواناً دقيقاً للمهمة، اختر العضو المسؤول وحدد تاريخ الاستحقاق النهائي.',
        'قدر عدد الساعات المطلوبة للإنجاز (مثلاً: 8 ساعات).',
        'أثناء العمل، حدث نسبة التقدم وسجل الساعات الفعلية المستغرقة.',
        'حول الحالة إلى "منجزة" فور اعتماد المخرجات المطلوبة.'
      ]
    },
    milestones: {
      title: 'المراحل البارزة والأهداف الرئيسية',
      description: 'متابعة المراحل المفصلية ونقاط المراقبة الأساسية لكل مشروع. يضمن الالتزام بالتعهدات التعاقدية واعتماد الانتقال بين مراحل الإنجاز.',
      features: [
        'حالات معيارية للمراحل: في الانتظار، تم التحقيق، متأخرة',
        'تاريخ الاستحقاق مع عد تنازلي تلقائي للأيام المتبقية قبل موعد المرحلة',
        'ربط مباشر بالمشروع وتحديد المخرجات التعاقدية المرتبطة به',
        'مخطط زمني مرتب لأبرز المحطات والالتزامات الكبرى للمؤسسة',
        'توثيق معايير القبول الفني اللازمة لاعتماد واجتياز المرحلة'
      ],
      tips: [
        'لا ينبغي أن يتجاوز المشروع العادي من 4 إلى 8 مراحل بارزة للحفاظ على وضوح الرؤية الاستراتيجية.',
        'أي مرحلة لم تتحقق بحلول تاريخها المستهدف تتحول تلقائياً إلى "متأخرة" وتخطر الإدارة.',
        'اربط اعتماد المرحلة بإصدار دفعة الفوترة المقابلة لتأمين التدفقات المالية للمشروع.'
      ],
      steps: [
        'أنشئ مرحلة بارزة بعنوان واضح (مثل: "اعتماد دفتر الشروط"، "تسليم الشطر الأول"، "الاستلام المؤقت").',
        'اربط المرحلة بمشروعها وحدد تاريخ الاستحقاق التعاقدي.',
        'فصل شروط ومعايير القبول في خانة الوصف.',
        'بمجرد الحصول على الاعتمادات الرسمية، حول الحالة إلى "تم التحقيق".'
      ]
    },
    team: {
      title: 'فريق العمل وموارد المشروع',
      description: 'تنظيم الموارد البشرية وتوزيع الكفاءات على مختلف المشاريع. تحديد الأدوار بدقة، قياس نسب التفرغ والجهد، وتجنب الإجهاد الزائد للموظفين.',
      features: [
        'دليل أعضاء الفريق المعينين لكل مشروع مع تحديد الدور الوظيفي (مدير مشروع، مهندس، تقني)',
        'نسبة التفرغ التقديرية المئوية (% من الوقت المخصص للمشروع)',
        'بيانات الاتصال المهنية (البريد الإلكتروني المباشر، الهاتف)',
        'رؤية شاملة وموحدة لتوافر كفاءات المؤسسة وأعبائها الحالية',
        'متابعة التعيينات عبر عدة مشاريع في وقت واحد للتحكم في الضغط التشغيلي'
      ],
      tips: [
        'احرص على ألا يتجاوز مجموع نسب تفرغ الموظف عبر كافة مشاريعه النشطة نسبة 100%.',
        'حدد مدير المشروع المرجعي بوضوح لكل فريق عمل لتسهيل اتخاذ القرارات وحل الإشكالات.',
        'عدل نسبة التفرغ خلال فترات الذروة والتسليم لتحقيق التوازن العادل في أعباء العمل.'
      ],
      steps: [
        'انقر على "عضو جديد" لتعيين كفاءة بشرية لمشروع معين.',
        'اختر المشروع المستهدف وأدخل الاسم الكامل والبريد الإلكتروني للموظف.',
        'حدد دوره ومسؤولياته الدقيقة في المشروع.',
        'حدد نسبة التفرغ المئوية (مثلاً: 50% للعمل بنصف دوام، 100% للتفرغ الكامل).',
        'احفظ لتأكيد تعيين المورد البشري على المشروع.'
      ]
    }
  }
};
